<template>
    <div id="lx-online-booking-checkout" class="lx-online-booking-checkout">
        <b-container fluid v-if="client && reservation">
            <validation-observer ref="form" v-slot="{ handleSubmit, meta, errors }" validateOnMount>
                <b-form @submit.prevent="handleSubmit(_, book)">
                    <b-row>
                        <b-col lg="4" order="last" order-lg="2" class="sticky-column">
                            <b-row>
                                <b-col md="12" style="margin-bottom: 30px">
                                    <online-booking-checkout-review-booking-card />
                                </b-col>
                                <b-col md="12" style="margin-bottom: 30px">
                                    <online-booking-checkout-review-cancellation-policies-card
                                        v-if="isCancellationPolicySet"
                                    />
                                </b-col>
                                <b-col md="12" style="margin-bottom: 30px">
                                    <online-booking-checkout-complete-booking-card
                                        :invalid="!meta.valid"
                                        :fields="getInvalidFields(errors)"
                                    />
                                </b-col>
                            </b-row>
                        </b-col>
                        <b-col md="12" lg="8" order-lg="1">
                            <b-row>
                                <b-col md="12">
                                    <online-booking-checkout-travel-details />
                                </b-col>
                                <b-col md="12" style="margin-bottom: 30px">
                                    <online-booking-checkout-client-details />
                                </b-col>
                                <b-col md="12" style="margin-bottom: 30px">
                                    <online-booking-checkout-rental-agreement
                                        v-if="!rentalAgreementBusy && rentalAgreement"
                                        :errors="errors"
                                    />
                                    <online-booking-checkout-review-policies
                                        v-else-if="!rentalAgreementBusy && !rentalAgreement"
                                    />
                                </b-col>
                                <b-col md="12" style="margin-bottom: 30px">
                                    <online-booking-checkout-payment @submitPayment="submitPayment" ref="obcp" />
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>
                    <online-booking-checkout-mobile-footer
                        class="lx-online-booking-checkout__mobile-footer"
                        @open="openPopup"
                    />
                    <online-booking-checkout-mobile-popup
                        class="lx-online-booking-checkout__mobile-popup"
                        :active="isPopupVisible"
                        :invalid="!meta.valid"
                        :fields="getInvalidFields(errors)"
                        @close="closePopup"
                    />
                </b-form>
            </validation-observer>
            <modal
                :id="eCheckConfirmationModalId"
                title="Payment Method Confirmation"
                ok-title="Accept"
                :ok-disabled="acceptEcheckPaymentDisabled"
                @ok.prevent="acceptEcheckPaymentMethod"
            >
                <busy-modal :show="acceptEcheckPaymentDisabled"></busy-modal>
                <b-row>
                    <b-col>
                        <b>Agreement Date:</b>
                    </b-col>
                    <b-col cols="auto">
                        <b>{{ eCheckAgreementDate }}</b>
                    </b-col>
                </b-row>
                <b-row class="pt-2">
                    <b-col>
                        <b>Full Name:</b>
                    </b-col>
                    <b-col cols="auto">
                        <b>{{ client.echeck_account_name }}</b>
                    </b-col>
                </b-row>
                <b-row class="pt-2">
                    <b-col>
                        <b>Financial Institution:</b>
                    </b-col>
                    <b-col cols="auto">
                        <b>{{ eCheckBankName }}</b>
                    </b-col>
                </b-row>
                <b-row class="pt-2">
                    <b-col>
                        <b>Routing Number:</b>
                    </b-col>
                    <b-col cols="auto">
                        <b>{{ eCheckRoutingNumber }}</b>
                    </b-col>
                </b-row>
                <b-row class="pt-2">
                    <b-col>
                        <b>Account Number:</b>
                    </b-col>
                    <b-col cols="auto">
                        <b>{{ eCheckAccountNumber }}</b>
                    </b-col>
                </b-row>
                <b-row class="pt-4">
                    <b-col>{{ eCheckMandateMessage }}</b-col>
                </b-row>
            </modal>
        </b-container>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import API from '../../../api';
import format from 'date-fns/format';
import { ONLINE_BOOKING_CLIENT_LIST_ENDPOINT } from '../../../endpoints';
import { maskNumber } from '../../../utils/strings';
import Card from '../../generic/card/card.vue';
import Modal from '../../generic/modal/modal.vue';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import OnlineBookingCheckoutCompleteBookingCard from './complete-booking/online-booking-checkout-complete-booking-card.vue';
import OnlineBookingCheckoutPayment from './payment/online-booking-checkout-payment.vue';
import OnlineBookingCheckoutReviewBookingCard from './review-booking/online-booking-checkout-review-booking-card.vue';
import OnlineBookingCheckoutTravelDetails from './travel-details/online-booking-checkout-travel-details.vue';
import OnlineBookingCheckoutClientDetails from './client-details/online-booking-checkout-client-details.vue';
import OnlineBookingCheckoutRentalAgreement from './rental-agreement/online-booking-checkout-rental-agreement.vue';
import OnlineBookingCheckoutReviewPolicies from './review-policies/online-booking-checkout-review-policies.vue';
import OnlineBookingCheckoutReviewCancellationPoliciesCard from './review-cancellation-policies/online-booking-checkout-review-cancellation-policies-card.vue';
import OnlineBookingCheckoutMobileFooter from './mobile/online-booking-checkout-mobile-footer.vue';
import OnlineBookingCheckoutMobilePopup from './mobile/online-booking-checkout-mobile-popup.vue';
import { PAYMENT_METHOD_CREDIT_CARD, convertFromBraintreeCardType } from '../../../constants';

export default {
    name: 'OnlineBookingCheckout',
    components: {
        Card,
        Modal,
        BusyModal,
        OnlineBookingCheckoutCompleteBookingCard,
        OnlineBookingCheckoutPayment,
        OnlineBookingCheckoutReviewBookingCard,
        OnlineBookingCheckoutTravelDetails,
        OnlineBookingCheckoutClientDetails,
        OnlineBookingCheckoutRentalAgreement,
        OnlineBookingCheckoutReviewPolicies,
        OnlineBookingCheckoutReviewCancellationPoliciesCard,
        OnlineBookingCheckoutMobileFooter,
        OnlineBookingCheckoutMobilePopup,
    },
    computed: {
        ...mapGetters('onlineBooking', [
            'client',
            'reservation',
            'stripeClientSecret',
            'website',
            'rentalAgreement',
            'rentalAgreementBusy',
        ]),
        ...mapGetters('stripe', ['stripe', 'card']),
        ...mapGetters('header', ['userUnicodeDateFormat']),
        quoteId() {
            return this.$route.params.quoteId || 0;
        },
        isAcceptJS() {
            return !!this.acceptJsSrcScript && !(this.reservation?.is_proxy && this.client.payment_method_id === 4);
        },
        isAcceptJSPaymentMethod() {
            return [4, 9].includes(this.client.payment_method_id);
        },
        isCancellationPolicySet() {
            return (
                this.reservation.cancellation_policy.cancellation_fee > 0 ||
                this.reservation.cancellation_policy.is_refunds_set
            );
        },
    },
    data() {
        return {
            loading: false,
            stripeConfirmed: false,
            isPopupVisible: false,
            setupIntent: null,
            eCheckAgreementDate: null,
            eCheckBankName: null,
            eCheckRoutingNumber: null,
            eCheckAccountNumber: null,
            eCheckMandateMessage: `You have authorized Lodgix to debit your bank account periodically
                    if and when you use Lodgix services or purchase more than one of Lodgix products 
                    periodically pursuant to Lodgix terms. Payments that fall outside of the regular debits 
                    authorized above will only be debited after your authorization is obtained.`,
            eCheckConfirmationModalId: 'echeck-confirmation-modal-id',
            acceptEcheckPaymentDisabled: false,
            showPendingConfirmationModal: false,
            acceptJsSrcScript: window.ACCEPT_JS_SRC_SCRIPT,
            authNetUserID: window.AUTH_NET_USER_ID,
            authNetPublishableKey: window.AUTH_NET_PUBLISHABLE_KEY,
        };
    },
    mounted() {
        (async () => {
            this.$store.commit('onlineBooking/gotBusy', true);
            try {
                if (!this.reservation && this.quoteId) {
                    await this.fetchQuoteReservation();
                    this.$router.replace({
                        name: 'online-booking-checkout',
                        params: {
                            websiteUrl: this.website.url,
                        },
                    });
                } else if (!this.reservation) {
                    this.$router.push({ name: 'online-booking-calendar' });
                }
            } finally {
                this.$store.commit('onlineBooking/gotBusy', false);
            }
        })();
        if (this.acceptJsSrcScript) {
            this.loadAccept(this.acceptJsSrcScript);
        }
    },
    methods: {
        ...mapActions('accept', ['loadAccept', 'dispatchAcceptData']),
        formatDate(value) {
            return value && format(new Date(value * 1000), this.userUnicodeDateFormat);
        },
        async fetchQuoteReservation() {
            const id = this.quoteId;
            try {
                await this.$store.dispatch('onlineBooking/fetchQuoteReservation', { id: id, withClient: true });
            } catch {
                await this.$router.push({
                    name: 'online-booking-calendar',
                });
                this.errorToast({
                    text:
                        "I'm sorry but your quote has expired." +
                        ' This most commonly occurs when the property is no longer available.' +
                        ' Please contact the property manager for additional instructions.',
                });
            }
        },
        async book() {
            this.client.meta_reservation_id = this.reservation.id;
            if (this.reservation.tech_reservations.length > 0) {
                if (this.client.payment_method_id == 4 && !!this.stripeClientSecret) {
                    if (!this.stripeConfirmed) {
                        this.$store.commit('onlineBooking/gotBusy', true);
                        try {
                            const res = await this.stripe.confirmCardSetup(this.stripeClientSecret, {
                                payment_method: {
                                    card: this.card,
                                    billing_details: {
                                        name: this.client.card_name,
                                    },
                                },
                            });
                            if (res.error) {
                                this.errorToast({
                                    text: res.error.message,
                                });
                                return;
                            }
                        } catch {
                            this.errorToast({
                                text: 'Something went wrong while confirming card.',
                            });
                            return;
                        } finally {
                            this.$store.commit('onlineBooking/gotBusy', false);
                        }
                    }

                    this.stripeConfirmed = true;
                    this.client.card_expiry = '12/99';
                    this.createClient();
                } else if (this.client.payment_method_id == 9 && !!this.stripeClientSecret) {
                    this.saveStripeEcheck();
                } else if (this.isAcceptJS && this.isAcceptJSPaymentMethod) {
                    if (!this.authNetUserID || !this.authNetPublishableKey) {
                        this.errorToast({
                            text: 'Error when connecting to Authorize.net. Please try again later.',
                        });
                        return;
                    }

                    const secureData = {};
                    secureData.authData = {
                        apiLoginID: this.authNetUserID,
                        clientKey: this.authNetPublishableKey,
                    };

                    if (this.client.payment_method_id == 4) {
                        const [month, year] = this.client.card_expiry.split('/');
                        secureData.cardData = {
                            cardNumber: this.client.card_number.replaceAll(' ', ''),
                            month: month,
                            year: year,
                            cardCode: this.client.card_code,
                            fullName: this.client.card_name,
                        };
                    } else {
                        secureData.bankData = {
                            accountNumber: this.client.echeck_account_no,
                            routingNumber: this.client.echeck_routing_no,
                            nameOnAccount: this.client.echeck_account_name,
                            accountType: this.client.echeck_account_type,
                            bankName: this.client.echeck_bank_name,
                        };
                    }

                    this.$store.commit('onlineBooking/gotBusy', true);
                    try {
                        const response = await this.dispatchAcceptData(secureData);
                        this.client.data_descriptor = response.opaqueData.dataDescriptor;
                        this.client.data_value = response.opaqueData.dataValue;
                    } catch {
                        this.errorToast({
                            text: 'Something went wrong while confirming payment information.',
                        });
                        return;
                    } finally {
                        this.$store.commit('onlineBooking/gotBusy', false);
                    }
                    this.createClient();
                } else if (this.reservation.is_proxy && this.client.payment_method_id == 4) {
                    const response = await this.$refs.obcp.$refs.capture.$refs.capture.submit();
                } else {
                    this.createClient();
                }
            }
        },
        async submitPayment() {
            this.createClient();
        },
        async createClient() {
            this.$store.commit('onlineBooking/gotBusy', true);
            try {
                const payload = {
                    ...this.client,
                    card_number: this.client.card_number.replaceAll(' ', ''),
                };
                if (this.client.payment_method_id == 9 && !!this.stripeClientSecret) {
                    this.acceptEcheckPaymentDisabled = true;
                    payload.echeck_account_name = this.client.echeck_account_name;
                    payload.echeck_account_type = this.setupIntent.payment_method.us_bank_account.account_type;
                    payload.echeck_check_type = this.setupIntent.payment_method.us_bank_account.account_holder_type;
                    payload.echeck_routing_no = this.setupIntent.payment_method.us_bank_account.routing_number;
                    payload.echeck_account_no = 'XXXX' + this.setupIntent.payment_method.us_bank_account.last4;
                    payload.echeck_bank_name = this.setupIntent.payment_method.us_bank_account.bank_name;
                } else if (this.isAcceptJS && this.isAcceptJSPaymentMethod) {
                    if (payload.payment_method_id === 4) {
                        payload.card_number = maskNumber(payload.card_number).slice(-8);
                        payload.card_code = 'XXX';
                        payload.echeck_routing_no = '';
                        payload.echeck_account_no = '';
                        payload.echeck_account_name = '';
                        payload.echeck_bank_name = '';
                    } else {
                        payload.card_number = '';
                        payload.card_code = '';
                        payload.echeck_routing_no = 'XXXXXXXXX';
                        payload.echeck_account_no = maskNumber(this.client.echeck_account_no).slice(-8);
                        payload.echeck_account_name = 'XXXXXXXXX';
                        payload.echeck_bank_name = 'XXXXXXXXX';
                    }
                }
                const response = await API.post(ONLINE_BOOKING_CLIENT_LIST_ENDPOINT(this.website.url), payload);

                if (this.client.payment_method_id == 9 && !!this.stripeClientSecret) {
                    this.$bvModal.hide(this.eCheckConfirmationModalId);
                }
                if (response.data.url) {
                    window.top.location = response.data.url;
                } else if (response.data.id) {
                    window.location = `/${this.website.url}/reservation/${response.data.id}`;
                } else {
                    this.errorToast({
                        text: 'Your session has expired. Please start over.',
                    });
                }
            } catch (error) {
                let msg = null;
                if (error.response.data.messages) {
                    msg = error.response.data.messages.join(' ');
                }
                if (msg == 'GUEST_CREDIT_DOES_NOT_EXIST') {
                    this.$refs.form.setErrors({
                        guest_credit_code: 'Wrong guest credit code.',
                    });
                    this.errorToast({
                        text: 'Wrong guest credit code.',
                    });
                } else {
                    this.errorToast({
                        title: 'Could not book trip',
                        text: msg || 'Your session has expired. Please start over.',
                    });
                }
            } finally {
                this.$store.commit('onlineBooking/gotBusy', false);
            }
        },
        async saveStripeEcheck() {
            try {
                this.stripe
                    .collectBankAccountForSetup({
                        clientSecret: this.stripeClientSecret,
                        params: {
                            payment_method_type: 'us_bank_account',
                            payment_method_data: {
                                billing_details: {
                                    name: this.client.echeck_account_name,
                                    email: this.client.echeck_holder_email,
                                },
                            },
                        },
                        expand: ['payment_method'],
                    })
                    .then(({ setupIntent, error }) => {
                        if (error) {
                            this.$store.commit('onlineBooking/gotBusy', false);
                            this.errorToast({ text: error.message });
                        } else if (setupIntent.status === 'requires_payment_method') {
                        } else if (setupIntent.status === 'requires_confirmation') {
                            this.setupIntent = setupIntent;
                            if ('us_bank_account' in setupIntent.payment_method) {
                                this.showEcheckConfirmation();
                            }
                        }
                    });
            } catch (err) {
                this.errorToast({ text: err });
                return;
            }
        },
        showEcheckConfirmation() {
            this.acceptEcheckPaymentDisabled = false;
            this.eCheckAgreementDate = this.formatDate(this.setupIntent?.created);
            this.eCheckBankName = this.setupIntent?.payment_method?.us_bank_account?.bank_name;
            this.eCheckRoutingNumber = this.setupIntent?.payment_method?.us_bank_account?.routing_number;
            this.eCheckAccountNumber = `XXXX${this.setupIntent?.payment_method?.us_bank_account?.last4}`;
            this.$bvModal.show(this.eCheckConfirmationModalId);
        },
        acceptEcheckPaymentMethod() {
            this.acceptEcheckPaymentDisabled = true;
            this.stripe.confirmUsBankAccountSetup(this.stripeClientSecret, {}).then(async (result) => {
                if (result.error) {
                    this.errorToast({ text: result.error.message });
                } else {
                    if (['requires_action', 'requires_source_action'].includes(result.setupIntent.status)) {
                        // window.open(
                        //     result.setupIntent?.next_action?.verify_with_microdeposits?.hosted_verification_url,
                        //     '_blank'
                        // );
                        this.acceptEcheckPaymentDisabled = false;
                        this.showPendingConfirmationModal = true;
                    }
                    if (result.setupIntent.status.includes('succeeded')) {
                    }
                    this.createClient();
                }
            });
        },
        openPopup() {
            this.isPopupVisible = true;
        },
        closePopup() {
            this.isPopupVisible = false;
        },
        getInvalidFields(errors) {
            const mapping = {
                s2_02_first_name: 'First Name',
                s2_03_last_name: 'Last Name',
                s2_04_email: 'Email',
                s2_05_email_confirm: 'Confirm Email',
                s2_00_date_of_birth: 'Date of Birth',
                s2_06_address1: 'Address',
                s2_08_country: 'Country',
                s2_09_zip: 'ZIP/Postal Code',
                s2_10_city: 'City',
                s2_11_state: 'State',
                s2_12_mobile_phone: 'Mobile Phone',
                s2_14_custom_field: 'Custom Field',
                channex_card_capture: 'Credit Card',
                guest_credit_code: 'Guest Credit Code',
                gift_certificate_no: 'Gift Certificate',
                echeck_routing_no: 'Routing Number',
                echeck_account_no: 'Account Number',
                echeck_account_name: 'Account Name',
                echeck_bank_name: 'Bank Name',
                check_no: 'Check Number',
                card_number: 'Card Number',
                card_code: 'Card Code',
                card_expiry: 'Card Expiry',
                card_name: 'Cardholder Name',
                stripe_setup_intent: 'Credit Card',
                policy: 'Terms And Conditions'
            };
            const getGuestFieldsString = (guestName) => {
                const match = guestName.match(/guest_names\[(\d+)\]\.(name|email|age)/);
                if (match && match[1] !== undefined) {
                    const guestIndex = parseInt(match[1]) + 1;
                    return `Guest #${guestIndex} ${match[2]}`;
                }
            };
            return Object.entries(errors).map(([key, value]) => ({
                id: key,
                name: key.indexOf('guest_names') !== -1 ? getGuestFieldsString(key) : mapping[key] || key,
                required: true,
                invalid: true,
            }));
        },
    },
    watch: {
        isPopupVisible(newVal, oldVal) {
            newVal
                ? (document.documentElement.style.overflow = 'hidden')
                : (document.documentElement.style.overflow = 'auto');
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.lx-online-booking-checkout {
    padding-bottom: 80px;

    .sticky-column {
        display: none;
    }
}

@include media-breakpoint-up(lg) {
    .sticky-column {
        display: block !important;
        position: sticky !important;
        height: 100%;
        top: 0.5rem;
        z-index: 100;
    }
    .lx-online-booking-checkout {
        padding-bottom: 0;

        &__mobile-footer {
            display: none;
        }
        &__mobile-popup {
            display: none;
        }
    }
}
</style>
